/* eslint-disable max-lines */
import { skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { Checkbox } from '@strata/react-checkbox-list';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { InlineMessage } from '@strata/react-inline-message';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuccessToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import { Formik, validateYupSchema, yupToFormErrors } from 'formik';
import * as Yup from 'yup';

import { usePermissionCheck } from 'services/APIHooks';
import { Permissions, YesNoIDK, YesNoIDKRadioOptions, YesNoRadioOptions } from 'services/constants';
import { handleError } from 'services/Error';
import { useAppDispatch } from 'store';
import { useGetNeedsByDateQuery, useUpdateNeedsByDateMutation } from 'store/api/performanceApi';
import { type ReferralDetails } from 'store/api/treatmentApi';
import { usePostConfirmNeedsByDateTasksMutation } from 'store/api/worklistApi';
import { fetchWorklistTasks } from 'store/worklist/worklistThunks';

const ReasonOptionLists = [
    [
        "Can't reach member",
        "Can't reach prescriber",
        'Drug shortage',
        'Financial assistance issues',
        'Member needs appointment',
        'Member refuses service',
        'Prescriber refuses to send script',
        'Waiting for lab work',
    ],
    ['Drug holiday', 'Future service start date', 'Medication on hand'],
    [
        "Can't reach member",
        "Can't reach prescriber",
        'Discontinued therapy',
        'Drug holiday',
        'Drug shortage',
        'Financial assistance issues',
        'Future service start date',
        'Medication on hand',
        'Member needs appointment',
        'Member refuses service',
        'Prescriber refuses to send script',
        'Termed coverage',
        'Waiting for lab work',
    ],
];

/**
 * Represents the tree of questions and answers
 * If a question's options are defined, then each option's value is a branch
 * If a key in a branch is a "reason" key, then it should have reasonOptions
 * A leaf should only have a single reason key
 */
const FormTree = {
    is_correct: {
        options: {
            [YesNoIDK.YES]: {
                can_meet: {
                    options: {
                        [YesNoIDK.NO]: {
                            reason: {
                                reasonOptions: ReasonOptionLists[0],
                            },
                            begin_conversation: {},
                        },
                    },
                },
            },
            [YesNoIDK.NO]: {
                know_correct: {
                    options: {
                        [YesNoIDK.YES]: {
                            needs_by_date: {},
                            reason: {
                                reasonOptions: ReasonOptionLists[1],
                            },
                        },
                        [YesNoIDK.NO]: {
                            begin_conversation: {},
                            reason: {
                                reasonOptions: ReasonOptionLists[2],
                            },
                        },
                    },
                },
            },
            [YesNoIDK.IDK]: {
                reason: {
                    reasonOptions: ReasonOptionLists[2],
                },
                begin_conversation: {},
            },
        },
    },
};

const FieldSchemas = {
    is_correct: Yup.string().required(validationStrings.requiredField.errorMessage),
    can_meet: Yup.string().required(validationStrings.requiredField.errorMessage),
    know_correct: Yup.string().required(validationStrings.requiredField.errorMessage),
    needs_by_date: DateInputFormik.validator().required(
        validationStrings.requiredField.errorMessage,
    ),
    reason: Yup.string().required(validationStrings.requiredField.errorMessage),
    begin_conversation: Yup.boolean().required(validationStrings.requiredField.errorMessage),
};
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const FieldSchemaObject = Yup.object(FieldSchemas);
type FieldValues = Yup.InferType<typeof FieldSchemaObject>;

const getVisibleFieldsAndReasons = (
    values: FieldValues,
    tree: Record<string, unknown> = FormTree,
) => {
    let visibleFields = new Set<keyof typeof FieldSchemas>();
    let reasons: string[] | undefined;
    Object.keys(tree).forEach((key) => {
        visibleFields.add(key as keyof typeof FieldSchemas);
        if (key === 'reason') {
            reasons = (tree[key] as { reasonOptions: string[] }).reasonOptions;
        }
        const branch = (tree[key] as { options: Record<string, unknown> } | undefined)?.options?.[
            values[key as keyof FieldValues] as string
        ];
        if (branch) {
            const { visibleFields: branchVisibleFields, reasons: branchReasons } =
                getVisibleFieldsAndReasons(values, branch as Record<string, unknown>);
            visibleFields = new Set([...visibleFields, ...branchVisibleFields]);
            if (branchReasons) {
                reasons = branchReasons;
            }
        }
    });
    return { visibleFields, reasons };
};

const getNewValuesWithConversationDefault =
    (changedValues: Partial<FieldValues>) =>
    (formikValues: FieldValues): FieldValues => {
        // Apply the changed values to the formik values
        const newValues = { ...formikValues, ...changedValues };
        // Get the new visible fields from the changes
        const { visibleFields: newVisibleFields } = getVisibleFieldsAndReasons(newValues);
        // Build a new object with only the visible fields and their values
        return {
            ...(Object.fromEntries(
                [...newVisibleFields].map((field) => [field, newValues[field]]),
            ) as FieldValues),
            // If tree has begin_conversation, set it to true by default
            begin_conversation: newVisibleFields.has('begin_conversation'),
        };
    };

const getSchema = (values: FieldValues, tree: Record<string, unknown> = FormTree) => {
    let schema: Record<string, Yup.AnySchema> = {};
    Object.keys(tree).forEach((key) => {
        if (key === 'reason') {
            schema = { ...schema, ...ReasonInput.validationSchema };
        } else {
            schema[key] = (FieldSchemas as Record<string, Yup.AnySchema>)[key];
        }
        const branch = (tree[key] as { options: Record<string, unknown> } | undefined)?.options?.[
            values[key as keyof FieldValues] as string
        ];
        if (branch) {
            schema = { ...schema, ...getSchema(values, branch as Record<string, unknown>) };
        }
    });
    return schema;
};

interface UpdateNeedsByDateDialogProps {
    isOpen: boolean;
    onClose: (beginConversation?: boolean) => void;
    referralDetails?: ReferralDetails;
}

export const UpdateNeedsByDateDialog = ({
    isOpen,
    onClose,
    referralDetails = undefined,
}: UpdateNeedsByDateDialogProps) => {
    const dispatch = useAppDispatch();
    const { data: needsByDate, isFetching: nbdFetching } = useGetNeedsByDateQuery(
        isOpen && referralDetails?.treatmentId
            ? { treatmentId: referralDetails.treatmentId }
            : skipToken,
    );
    const [updateNeedsByDate] = useUpdateNeedsByDateMutation();
    const [postConfirmNeedsByDateTasks] = usePostConfirmNeedsByDateTasksMutation();
    const writePerformance = usePermissionCheck(Permissions.WRITE_PERFORMANCE);

    if (isOpen && nbdFetching) {
        return <FullscreenLoading loadingText="Loading..." />;
    }

    return (
        <Formik
            initialValues={{ begin_conversation: false } as FieldValues}
            validate={async (values) => {
                const schema = Yup.object().shape(getSchema(values));
                try {
                    await validateYupSchema(values, schema, true);
                } catch (err) {
                    return yupToFormErrors(err);
                }
                return {};
            }}
            onSubmit={async (values, { resetForm }) => {
                try {
                    if (referralDetails) {
                        await updateNeedsByDate({
                            treatmentId: referralDetails.treatmentId,
                            body: {
                                referral_id: referralDetails.referralId,
                                payer_id: referralDetails.payerId,
                                is_correct: values.is_correct,
                                can_meet: values.can_meet,
                                know_correct: values.know_correct,
                                needs_by_date: formatDate(
                                    values.needs_by_date,
                                    DateFormats.HYPHEN_DATE,
                                ),
                                begin_conversation: values.begin_conversation,
                                ...ReasonInput.submitValues(values),
                            },
                        }).unwrap();
                        await postConfirmNeedsByDateTasks({
                            referralId: referralDetails?.referralId,
                        }).unwrap();
                        await dispatch(fetchWorklistTasks());
                        SuccessToast({ message: 'Needs by date updated successfully.' });
                        resetForm();
                        onClose(values.begin_conversation);
                    }
                } catch (err) {
                    handleError('Unable to update needs by date. Please try again.', err as Error);
                }
            }}
        >
            {(formik) => {
                const { visibleFields, reasons } = getVisibleFieldsAndReasons(formik.values);
                return (
                    <DialogWithForm
                        isOpen={isOpen}
                        dialogTitle="Confirm/Update Needs By Date"
                        secondaryButtonLabel="Cancel"
                        primaryButtonLabel={
                            formik.values.begin_conversation
                                ? 'Save And Create Conversation'
                                : 'Save'
                        }
                        isCloseButtonShown
                        onSecondaryButtonClick={() => {
                            onClose();
                            formik.handleReset();
                        }}
                        isPrimaryButtonDisabled={
                            !(formik.isValid && formik.dirty) ||
                            formik.isSubmitting ||
                            !writePerformance
                        }
                        canEscapeKeyClose
                        width={600}
                        dialogContent={
                            <>
                                <RadioButtonGroupFormik
                                    name="is_correct"
                                    label={`Is "${formatDate(
                                        needsByDate?.needsByDate,
                                    )}" the correct needs by date for this patient?`}
                                    options={YesNoIDKRadioOptions}
                                    disabled={!writePerformance}
                                    onChange={(value) => {
                                        void formik.setValues(
                                            getNewValuesWithConversationDefault({
                                                is_correct: value,
                                            }),
                                        );
                                    }}
                                    required
                                />
                                {visibleFields.has('can_meet') && (
                                    <RadioButtonGroupFormik
                                        name="can_meet"
                                        label="Can you meet this needs by date?"
                                        options={YesNoRadioOptions}
                                        disabled={!writePerformance}
                                        onChange={(value) => {
                                            void formik.setValues(
                                                getNewValuesWithConversationDefault({
                                                    can_meet: value,
                                                }),
                                            );
                                        }}
                                        required
                                    />
                                )}
                                {visibleFields.has('know_correct') && (
                                    <RadioButtonGroupFormik
                                        name="know_correct"
                                        label="Do you know the correct needs by date?"
                                        options={YesNoRadioOptions}
                                        disabled={!writePerformance}
                                        onChange={(value) => {
                                            void formik.setValues(
                                                getNewValuesWithConversationDefault({
                                                    know_correct: value,
                                                }),
                                            );
                                        }}
                                        required
                                    />
                                )}
                                {visibleFields.has('needs_by_date') && (
                                    <>
                                        <div className="padding__bottom--medium">
                                            Update the needs by date for this patient below.
                                        </div>
                                        <DateInputFormik
                                            id="needs_by_date"
                                            label="Needs By Date"
                                            disabled={!writePerformance}
                                            required
                                        />
                                    </>
                                )}
                                {reasons && <ReasonInput reasons={reasons} hideSource />}
                                {visibleFields.has('begin_conversation') && (
                                    <div className="padding__top--small">
                                        <div className="padding__bottom--small">
                                            <InlineMessage
                                                label="It is highly recommended to begin a support conversation for further assistance."
                                                intent={InlineMessage.Intent.INFO}
                                                wrapText
                                            />
                                        </div>
                                        <Checkbox
                                            name="begin_conversation"
                                            label="Begin a support conversation"
                                            disabled={!writePerformance}
                                            checked={formik.values.begin_conversation}
                                            onChange={(checked) => {
                                                void formik.setFieldValue(
                                                    'begin_conversation',
                                                    checked,
                                                );
                                            }}
                                        />
                                    </div>
                                )}
                            </>
                        }
                    />
                );
            }}
        </Formik>
    );
};
